import { t } from '../../strings';
import { weekdayInitial } from '../../strings/format';
import type { RegionManifest, Skill } from '../../types';
import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import { donutRing } from '../components/donut-ring';
import {
  activePacks,
  fetchPackManifest,
  getSpecies,
  loadPackIndex,
  region,
} from '../../core/region';
import { studyCodes, withoutIgnored } from '../../core/ignored';
import { packStatus } from '../../core/media/pack';
import type { Facet } from '../../core/compare';
import {
  accuracyBySkill,
  confusions,
  CONFUSION_DAYS,
  CONFUSION_MIN,
  type Confusion,
  dueForecast,
  streakStatus,
  getXp,
  masteryBySkill,
  reviewHistory,
  skillProgress,
  XP_DAILY_BONUS,
  XP_PER_CORRECT,
  type DayTally,
  type SkillProgress,
} from '../../core/progress';

// The Stats screen: every number the app shows anywhere, with the sentence that
// says what it means, plus the history the review log has always carried and
// nothing ever read back.
//
// It exists because the home screen is deliberately terse — a streak, three
// percentages, a coverage bar — and terse numbers invite the wrong reading. The
// rings in particular are NOT coverage (see masteryBySkill), which is impossible
// to guess from a ring, so the distinction gets stated here in full and the home
// screen links to it.
//
// The three rings are drawn here, not merely described: this section used to be
// headed "The three rings" and then show three stacked bars, which is the last
// place in the app that could afford a mismatch between what it says and what it
// draws.

ensureStyle(
  'stats-css',
  `
  .statspage { padding:16px var(--pad); display:flex; flex-direction:column; gap:16px; }
  .statspage h1 { font-size:30px; margin:0; }
  .statspage h2 { font-size:19px; margin:0; }
  .statspage .lede { margin:-6px 0 0; }
  .statspage section { display:flex; flex-direction:column; gap:11px; }
  /* One row per number: the value, its name, and the sentence explaining it. */
  .statrow { display:grid; grid-template-columns:auto 1fr; gap:4px 13px; align-items:baseline; }
  .statrow .n { font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:25px; line-height:1; grid-row:span 2; }
  .statrow .what { align-self:end; }
  .statrow .why { grid-column:2; }
  /* ---- Birds by pack -----------------------------------------------------
     Each pack on the device, and each quiz type's hold on it: one bar per
     skill, cut three ways. Bars here, where the rings above are deliberately
     not coverage meters — this section IS the coverage, so a bar that fills
     with the pack is the honest shape for it. Mastered is the skill's colour,
     met is the same colour faded, never met is the bare track, so the three
     read as one quantity at three stages rather than three unrelated things. */
  .packs { display:flex; flex-direction:column; gap:16px; }
  /* One grid per pack, the rows flattened into it, so the three bars start at
     the same x whatever the longest skill name is. */
  .packs .pack { display:grid; grid-template-columns:max-content 1fr; gap:3px 10px;
    align-items:center; }
  .packs .pack .name { grid-column:1 / -1; display:flex; align-items:baseline; gap:8px;
    flex-wrap:wrap; margin-bottom:4px; }
  .packs .pack .name b { font-family:var(--font-display); font-size:16px; font-weight:600; }
  .packs .srow { display:contents; }
  .packs .srow .who { display:flex; align-items:center; gap:7px; white-space:nowrap; }
  .packs .dot { width:9px; height:9px; border-radius:50%; flex:none; }
  .packs .sbar { display:flex; height:12px; border-radius:6px; overflow:hidden;
    background:var(--track); }
  .packs .sbar i { display:block; height:100%; }
  .packs .sbar i.met { opacity:.38; }
  .packs .srow .counts { grid-column:2; font-size:12px; margin-bottom:5px; }
  .packs .key { display:flex; flex-wrap:wrap; gap:4px 14px; font-size:12px; }
  .packs .key span { display:inline-flex; align-items:center; gap:6px; }
  .packs .key i { width:14px; height:9px; border-radius:3px; background:var(--ink-soft); }
  .packs .key i.met { opacity:.38; }
  .packs .key i.unmet { background:var(--track); }
  /* Column chart. Bars grow from a baseline; the correct share of each day is
     drawn inside the day's column rather than beside it, so the column height
     stays "how much did I do" and the fill is "how much of it was right". */
  .chart { display:flex; align-items:flex-end; gap:3px; height:96px;
    padding:9px 10px; background:var(--sunk); border-radius:var(--radius);
    border:1.5px solid var(--hairline); }
  .chart .col { flex:1; display:flex; flex-direction:column; justify-content:flex-end;
    align-items:center; gap:4px; height:100%; min-width:0; }
  /* min-height keeps a studied-nothing day visible as a stub on the baseline,
     so a gap reads as "no answers" rather than as a missing column. */
  .chart .col .bar { width:100%; max-width:22px; min-height:2px; background:var(--track);
    border-radius:3px 3px 1px 1px; position:relative; overflow:hidden;
    display:flex; flex-direction:column; justify-content:flex-end; }
  .chart .col .bar i { display:block; width:100%; background:var(--accent); }
  .chart .col .tick { font-family:var(--font-mono); font-size:8px; color:var(--ink-soft);
    white-space:nowrap; }
  .chart .col.today .tick { color:var(--accent); font-weight:700; }
  .chart.due .col .bar { background:var(--patina); }
  .statspage .foot { display:flex; gap:8px; flex-wrap:wrap; }
  /* ---- The mix-ups --------------------------------------------------------
     One row per pair, each a way into the drill for it and, beside it, into
     the side-by-side view. Rows rather than a
     chart: there are only ever a handful, and the two names ARE the content —
     a bar whose label is "Pacific Wren → Winter Wren" is a label with a bar
     stuck to it. */
  .mixups { display:flex; flex-direction:column; }
  .mixups .mixrow { display:flex; align-items:center; gap:6px;
    border-bottom:1.5px solid var(--hairline); }
  .mixups .mixrow:last-child { border-bottom:none; }
  .mixups .row { flex:1; min-width:0; display:flex; align-items:center; gap:10px;
    padding:10px 2px; text-align:left; background:none; border:0;
    color:var(--ink); cursor:pointer; }
  /* Beside the drill, never inside it — a control nested in a button is a tap
     that does both. Wordless, as it is under a quiz answer: the species page is
     where the glyph is spelled out. */
  .mixups .compare { flex:none; width:40px; height:40px; padding:0; gap:0;
    border-radius:999px; background:none; border:1.5px solid var(--hairline-strong);
    color:var(--ink-soft); }
  .mixups .compare:hover, .mixups .compare:focus-visible { color:var(--ink); }
  .mixups .dot { width:9px; height:9px; border-radius:50%; flex:none; }
  .mixups .pair { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
  .mixups .pair .nm { font-family:var(--font-display); font-size:15.5px; line-height:1.2; }
  .mixups .pair .sub { font-size:11.5px; color:var(--ink-soft); }
  .mixups .row .icon { flex:none; color:var(--ink-soft); }
`,
);

const SKILL_META: Record<
  Skill,
  { label: string; color: string; blurb: string; ring: string }
> = {
  sound: {
    label: t('common.skill.sound'),
    color: 'var(--skill-sound)',
    blurb: t('stats.blurb.sound'),
    // The ring caption is the one Home uses — the two screens have to be
    // recognisably the same three rings for the cross-reference to land.
    ring: t('common.skillRing.sound'),
  },
  names: {
    label: t('common.skill.names'),
    color: 'var(--skill-names)',
    blurb: t('stats.blurb.names'),
    ring: t('common.skillRing.names'),
  },
  photo: {
    label: t('common.skill.photo'),
    color: 'var(--skill-photo)',
    blurb: t('stats.blurb.photo'),
    ring: t('common.skillRing.photo'),
  },
};

const SKILL_ORDER: Skill[] = ['sound', 'names', 'photo'];

/** Where a skill's round lives — the mix-up rows open the drill in it. */
const SKILL_PATH: Record<Skill, string> = {
  sound: '/quiz/sound',
  names: '/quiz/names',
  photo: '/quiz/photo',
};

const HISTORY_DAYS = 14;
const FORECAST_DAYS = 7;

export const statsView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const manifest = region();
  const codes = studyCodes();
  const now = new Date();
  const [streak, xp, mastery, packs, history, accuracy, due, mixed] =
    await Promise.all([
      streakStatus(now),
      getXp(),
      masteryBySkill(codes, now),
      packsOnDevice().then((list) =>
        Promise.all(
          list.map(async (m) => {
            const own = withoutIgnored(m.species.map((sp) => sp.code));
            return { m, total: own.length, progress: await skillProgress(own, now) };
          }),
        ),
      ),
      reviewHistory(HISTORY_DAYS, now),
      accuracyBySkill(HISTORY_DAYS, now),
      dueForecast(codes, FORECAST_DAYS, now),
      confusions(CONFUSION_DAYS, now),
    ]);

  // A pair whose birds aren't loaded has no names to print — a mix-up recorded
  // while another pack was selected. It is still true, and it comes back with
  // the pack; there is simply nothing to show meanwhile.
  const mixups = mixed.filter((c) => getSpecies(c.code) && getSpecies(c.picked));

  const studied = history.reduce((n, d) => n + d.total, 0);
  const right = history.reduce((n, d) => n + d.correct, 0);

  root.append(
    el(
      'div.statspage',
      {},
      el('h1', {}, t('common.nav.stats')),
      el('p.lede.meta', {}, t('stats.lede')),

      // ---- The three headline numbers ------------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.numbers.title')),
        statRow(
          String(streak.days),
          t('stats.numbers.streak'),
          t('stats.numbers.streakWhy', { atRisk: String(streak.atRisk) }),
        ),
        statRow(
          String(xp),
          t('common.totalXp'),
          t('stats.numbers.xpWhy', { perCorrect: XP_PER_CORRECT, bonus: XP_DAILY_BONUS }),
        ),
        statRow(
          t('common.percent', { percent: mastery.overall }),
          t('common.mastery.label'),
          t('stats.numbers.masteryWhy'),
        ),
      ),

      // ---- What the rings actually measure --------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.rings.title')),
        el(
          'div.ringrow',
          {},
          ...SKILL_ORDER.map((skill) => {
            const m = mastery.bySkill[skill];
            const { label, color, ring } = SKILL_META[skill];
            return donutRing({
              percent: m.percent,
              caption: ring,
              color,
              // Solid out of MET — the same fraction Home prints under its
              // rings, and the numerator and denominator the split below then
              // takes apart.
              detail: t('common.mastery.ringDetail', { mastered: m.mastered, started: m.started }),
              label: t('common.mastery.ringLabel', {
                skill: label,
                percent: m.percent,
                started: m.started,
              }),
            });
          }),
        ),
        el('div.meta', {}, t('stats.rings.explain')),
        el(
          'div.meta',
          {},
          t('stats.rings.coverage', {
            started: mastery.speciesStarted,
            total: manifest.speciesCount,
            region: manifest.label,
          }),
        ),
      ),

      // ---- Every pack on the device, per quiz type -------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.packs.title')),
        el('div.meta', {}, t('stats.packs.intro')),
        el(
          'div.packs',
          {},
          el(
            'div.key.meta',
            { 'aria-hidden': 'true' },
            el('span', {}, el('i', {}), t('stats.packs.mastered')),
            el('span', {}, el('i.met', {}), t('stats.packs.met')),
            el('span', {}, el('i.unmet', {}), t('stats.packs.unmet')),
          ),
          ...packs.map(({ m, total, progress }) => packBlock(m, total, progress)),
        ),
      ),

      // ---- Study history --------------------------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.history.title', { days: HISTORY_DAYS })),
        historyChart(history),
        el(
          'div.meta',
          {},
          studied
            ? t('stats.history.summary', {
                answers: studied,
                percent: Math.round((right / studied) * 100),
              })
            : t('stats.history.empty'),
        ),
      ),

      // ---- Accuracy per skill ---------------------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.accuracy.title')),
        el('div.meta', {}, t('stats.accuracy.intro', { days: HISTORY_DAYS })),
        ...SKILL_ORDER.map((skill) => {
          const { percent, correct, total } = accuracy[skill];
          const { label, blurb } = SKILL_META[skill];
          return statRow(
            percent === null ? '—' : t('common.percent', { percent }),
            label,
            total
              ? t('stats.accuracy.studied', { correct, total, blurb })
              : t('stats.accuracy.unstudied', { days: HISTORY_DAYS, blurb }),
          );
        }),
      ),

      // ---- What you mix up with what --------------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.mixups.title')),
        el(
          'div.meta',
          {},
          mixups.length
            ? t('stats.mixups.intro', { days: CONFUSION_DAYS })
            : t('stats.mixups.empty', { min: CONFUSION_MIN, days: CONFUSION_DAYS }),
        ),
        mixups.length
          ? el('div.mixups', {}, ...mixups.slice(0, MIXUPS_SHOWN).map((c) => mixupRow(c, ctx)))
          : null,
      ),

      // ---- What the schedule wants next -----------------------------------
      el(
        'section.card',
        {},
        el('h2', {}, t('stats.due.title')),
        dueChart(due.days),
        el(
          'div.meta',
          {},
          due.overdue ? t('stats.due.overdue', { n: due.overdue }) : t('stats.due.none'),
        ),
      ),

      el(
        'div.foot',
        {},
        el(
          'button.btn.btn-quiet.btn-sm',
          { onclick: () => ctx.navigate('/you') },
          icon('user', { size: 16 }),
          t('stats.progressLink'),
        ),
      ),
    ),
  );
};

/** How many pairs the mix-up list prints before it stops. */
const MIXUPS_SHOWN = 6;

/**
 * Which section of the side-by-side view a mix-up opens on.
 *
 * A mix-up remembers its skill but not which voice the question played, so a
 * Sound ID pair lands on the song — the voice a round asks most — and the view
 * falls back on its own if either bird has none. Photos and names both start at
 * the top, where the photographs are.
 */
const SKILL_FACET: Record<Skill, Facet | undefined> = {
  sound: 'song',
  names: undefined,
  photo: undefined,
};

/**
 * One mix-up, the way into a drill for it, and the way to look at the two.
 *
 * Directional, as the data is: "you call the Pacific Wren a Winter Wren" is a
 * different sentence from its reverse, and the arrow is the whole point. The
 * drill it opens is not directional — it asks both ways — because the cure is.
 *
 * The compare beside it is the other cure. A drill tests the distinction; the
 * side-by-side view is where you learn it, and every row on this list is a
 * pair you have already proved you need it for. A navigation rather than the
 * quizzes' overlay, for the species page's reason: this screen holds no state
 * that going back would lose.
 */
function mixupRow(c: Confusion, ctx: ViewContext): HTMLElement {
  const asked = getSpecies(c.code);
  const named = getSpecies(c.picked);
  const { label, color } = SKILL_META[c.skill];
  const names = { asked: asked?.common ?? c.code, named: named?.common ?? c.picked };
  const facet = SKILL_FACET[c.skill];
  const drill = el(
    'button.row',
    {
      onclick: () => ctx.navigate(`${SKILL_PATH[c.skill]}?drill=pairs`),
      title: t('stats.mixups.drill', { ...names, skill: label }),
    },
    el('span.dot', { style: { background: color } }),
    el(
      'span.pair',
      {},
      el(
        'span.nm',
        {},
        t('stats.mixups.pair', names),
      ),
      el(
        'span.sub',
        {},
        t('stats.mixups.picks', {
          times: c.times,
          asked: c.asked,
          skill: label,
          percent: c.percent,
        }),
      ),
    ),
    icon('arrow-right', { size: 16 }),
  );
  const compare = el(
    'button.compare.btn',
    {
      type: 'button',
      title: t('stats.mixups.compare', names),
      'aria-label': t('stats.mixups.compare', names),
      onclick: () =>
        ctx.navigate(`/compare/${c.code}/${c.picked}${facet ? `?facet=${facet}` : ''}`),
    },
    icon('compare', { size: 18 }),
  );
  return el('div.mixrow', {}, drill, compare);
}

/** A number, its name, and the sentence that explains it. */
function statRow(n: string, what: string, why: string): HTMLElement {
  return el(
    'div.statrow',
    {},
    el('div.n', {}, n),
    el('div.what.label', {}, what),
    el('div.why.meta', {}, why),
  );
}

/**
 * The packs whose birds this section counts: the ones being studied, then any
 * other pack with media on this device.
 *
 * The selection first and always, downloaded or not — it is what every other
 * number on this screen is about, and a pack studied on synthetic tones is
 * still being studied. The rest only if some of their media is here: a pack
 * nobody has fetched is a catalogue entry, and a row of empty bars for it
 * would read as work left undone rather than work never started. Cards
 * outlive a selection, so a pack studied last month and switched away from
 * still shows what was learned in it.
 */
async function packsOnDevice(): Promise<RegionManifest[]> {
  const active = activePacks();
  const ids = new Set(active.map((m) => m.id));
  if (typeof caches === 'undefined') return active;
  const others = await Promise.all(
    (await loadPackIndex())
      .filter((p) => !ids.has(p.id))
      .map(async (p) => {
        try {
          const m = await fetchPackManifest(p.id);
          return (await packStatus(m)).have > 0 ? m : null;
        } catch {
          // A manifest that will not load has no birds to count.
          return null;
        }
      }),
  );
  return [...active, ...others.filter((m): m is RegionManifest => !!m)];
}

/** One pack: its name, and a bar per quiz type cut into mastered, met, never met. */
function packBlock(
  m: RegionManifest,
  total: number,
  progress: Record<Skill, SkillProgress>,
): HTMLElement {
  return el(
    'div.pack',
    {},
    el('div.name', {}, el('b', {}, m.label), el('span.meta', {}, t('stats.packs.birds', { n: total }))),
    ...SKILL_ORDER.map((skill) => {
      // The rings' short captions, so the label column leaves the counts a
      // line of their own on a phone; the full name is in the bar's label.
      const { label, color, ring } = SKILL_META[skill];
      const { solid, shaky, unmet } = progress[skill];
      const pct = (n: number) => `${total ? (n / total) * 100 : 0}%`;
      const counts = t('stats.packs.split', { mastered: solid, met: shaky, unmet });
      return el(
        'div.srow',
        {},
        el('span.who.label', {}, el('span.dot', { style: { background: color } }), ring),
        el(
          'div.sbar',
          { role: 'img', 'aria-label': t('stats.packs.barLabel', { skill: label, counts }) },
          el('i', { style: { width: pct(solid), background: color } }),
          el('i.met', { style: { width: pct(shaky), background: color } }),
        ),
        el('div.counts.meta', {}, counts),
      );
    }),
  );
}

/** Day-of-month for a YYYY-MM-DD key. */
function dayOfMonth(day: string): string {
  return String(Number(day.slice(8, 10)));
}

function historyChart(history: DayTally[]): HTMLElement {
  const peak = Math.max(1, ...history.map((d) => d.total));
  return el(
    'div.chart',
    {},
    ...history.map((d, i) =>
      el(
        `div.col${i === history.length - 1 ? '.today' : ''}`,
        {},
        el(
          'div.bar',
          {
            style: { height: `${(d.total / peak) * 100}%` },
            title: t('stats.history.bar', { day: d.day, total: d.total, correct: d.correct }),
          },
          el('i', {
            style: { height: d.total ? `${(d.correct / d.total) * 100}%` : '0%' },
          }),
        ),
        el('div.tick', {}, weekdayInitial(d.day)),
      ),
    ),
  );
}

function dueChart(days: { day: string; count: number }[]): HTMLElement {
  const peak = Math.max(1, ...days.map((d) => d.count));
  return el(
    'div.chart.due',
    {},
    ...days.map((d, i) =>
      el(
        `div.col${i === 0 ? '.today' : ''}`,
        {},
        el('div.bar', {
          style: { height: `${(d.count / peak) * 100}%` },
          title: t('stats.due.bar', { day: d.day, n: d.count }),
        }),
        el('div.tick', {}, i === 0 ? t('stats.due.today') : dayOfMonth(d.day)),
      ),
    ),
  );
}
